import { FaCalculator, FaSearch, FaSpinner } from 'react-icons/fa';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';

const Api3Form = ({ state, updateState }) => {
    const formData = state.formData || {};
    const CLIMATIQ_API_KEY = process.env.REACT_APP_CLIMATIQ_API_KEY || 'Y2ZBNWZKRzRGNzJYNjRQMzpa';

    const fuelSearchConfig = {
        naturalGas: { query: 'natural gas', preferred_unit_type: 'Volume' },
        diesel: { query: 'diesel', preferred_unit_type: 'Volume' },
        heavyFuelOil: { query: 'heavy fuel oil', preferred_unit_type: 'Energy' },
        lpg: { query: 'lpg', preferred_unit_type: 'Energy' },
        coal: { query: 'coal', preferred_unit_type: 'Energy' }
    };

    const conversionFactors = {
        naturalGas: { m3_to_kWh: 10.55 },
        heavyFuelOil: { L_to_kWh: 10.84 },
        lpg: { kg_to_kWh: 13.99 },
        coal: { t_to_kWh: 8100 }
    };

    const conversionRates = {
        naturalGas: 0.9, diesel: 1.2, heavyFuelOil: 0.8, lpg: 1.1, coal: 120
    };

    const fuelOptions = [
        { key: 'naturalGas', label: 'Natural gas (m³ - cubic meters)', description: 'Boilers/process heat, dryers, turbines, site heating', unit: 'm3', unitLabel: 'cubic meters' },
        { key: 'diesel', label: 'Diesel (L - liters)', description: 'Backup/prime power at remote sites, fixed compressors/pumps', unit: 'l', unitLabel: 'liters' },
        { key: 'heavyFuelOil', label: 'Heavy/Residual fuel oil (L - liters)', description: 'High-temperature process heat where gas isn\'t available', unit: 'l', unitLabel: 'liters' },
        { key: 'lpg', label: 'LPG / Propane (kg)', description: 'Space/water heating, small boilers, dryers in remote facilities', unit: 'kg', unitLabel: 'kilograms' },
        { key: 'coal', label: 'Coal (t - metric tons)', description: 'Legacy boilers and kilns at certain processing plants', unit: 't', unitLabel: 'metric tons' }
    ];

    const searchEmissionFactors = async (fuelKey) => {
        try {
            const config = fuelSearchConfig[fuelKey];
            const params = new URLSearchParams({
                query: config.query,
                data_version: '^3',
                region: formData.region || 'US',
                year: formData.year || new Date().getFullYear().toString(),
                results_per_page: '5'
            });

            const response = await fetch(`https://api.climatiq.io/data/v1/search?${params.toString()}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${CLIMATIQ_API_KEY}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) throw new Error(`Search API failed with status ${response.status}`);
            const data = await response.json();

            if (data.results && data.results.length > 0) {
                const stationaryFactors = data.results.filter(factor =>
                    factor.source_lca_activity === 'fuel_combustion' && factor.activity_id.includes('stationary')
                );
                let bestFactor = stationaryFactors[0] || data.results[0];

                if (config.preferred_unit_type) {
                    const preferredFactor = stationaryFactors.find(f => f.unit_type === config.preferred_unit_type)
                        || data.results.find(f => f.unit_type === config.preferred_unit_type);
                    if (preferredFactor) bestFactor = preferredFactor;
                }

                updateState({ availableFactors: { ...state.availableFactors, [fuelKey]: bestFactor } });
                return bestFactor;
            }
            return null;
        } catch (error) {
            console.error(`Error searching emission factors for ${fuelKey}:`, error);
            return null;
        }
    };

    const searchSpendFactors = async (fuelKey) => {
        try {
            const config = fuelSearchConfig[fuelKey];
            const params = new URLSearchParams({
                query: `${config.query} spend`,
                data_version: '^3',
                region: formData.region || 'US',
                year: formData.year || new Date().getFullYear().toString(),
                results_per_page: '5'
            });

            const response = await fetch(`https://api.climatiq.io/data/v1/search?${params.toString()}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${CLIMATIQ_API_KEY}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) throw new Error(`Search API failed with status ${response.status}`);
            const data = await response.json();

            if (data.results && data.results.length > 0) {
                const spendFactors = data.results.filter(factor => factor.unit_type === 'Money');
                let bestFactor = spendFactors[0] || data.results[0];

                updateState({ spendFactors: { ...state.spendFactors, [fuelKey]: bestFactor } });
                return bestFactor;
            }
            return null;
        } catch (error) {
            console.error(`Error searching spend factors for ${fuelKey}:`, error);
            return null;
        }
    };

    const handleSearchFactors = async () => {
        const selectedFuels = Object.entries(formData.fuels || {}).filter(([_, data]) => data.selected);
        if (selectedFuels.length === 0) {
            toastr.error('Please select at least one fuel type');
            return;
        }

        updateState({ isSearching: true, availableFactors: {}, spendFactors: {} });
        toastr.info('Searching for best emission factors...');

        try {
            let foundCount = 0;
            for (const [fuelKey] of selectedFuels) {
                const quantityFactor = await searchEmissionFactors(fuelKey);
                const spendFactor = await searchSpendFactors(fuelKey);
                if (quantityFactor || spendFactor) foundCount++;
            }

            if (foundCount > 0) {
                toastr.success(`Found emission factors for ${foundCount} fuel(s)! Click Calculate to proceed.`);
            } else {
                toastr.warning('No specific factors found for US. Please try different search parameters.');
            }
        } catch (error) {
            toastr.error('Search failed. Please check your API key and try again.');
        } finally {
            updateState({ isSearching: false });
        }
    };

    const callQuantityClimateIQAPI = async (fuelKey, quantity, emissionFactor) => {
        try {
            const userUnit = formData.fuels[fuelKey].unit;
            let apiValue = quantity;
            let apiUnit = userUnit;

            if (emissionFactor.unit_type === 'Energy') {
                if (fuelKey === 'naturalGas' && userUnit === 'm3') {
                    apiValue = quantity * conversionFactors.naturalGas.m3_to_kWh;
                    apiUnit = 'kWh';
                } else if (fuelKey === 'heavyFuelOil' && userUnit === 'l') {
                    apiValue = quantity * conversionFactors.heavyFuelOil.L_to_kWh;
                    apiUnit = 'kWh';
                } else if (fuelKey === 'lpg' && userUnit === 'kg') {
                    apiValue = quantity * conversionFactors.lpg.kg_to_kWh;
                    apiUnit = 'kWh';
                } else if (fuelKey === 'coal' && userUnit === 't') {
                    apiValue = quantity * conversionFactors.coal.t_to_kWh;
                    apiUnit = 'kWh';
                }
            }

            let parameterName, parameterUnit;
            if (emissionFactor.unit_type === 'Energy') {
                parameterName = 'energy';
                parameterUnit = 'energy_unit';
            } else if (emissionFactor.unit_type === 'Volume') {
                parameterName = 'volume';
                parameterUnit = 'volume_unit';
            } else {
                parameterName = 'weight';
                parameterUnit = 'weight_unit';
            }

            const requestBody = {
                emission_factor: {
                    activity_id: emissionFactor.activity_id,
                    source: emissionFactor.source,
                    region: emissionFactor.region,
                    year: parseInt(emissionFactor.year),
                    data_version: "^3"
                },
                parameters: {
                    [parameterName]: apiValue,
                    [parameterUnit]: apiUnit
                }
            };

            const response = await fetch('https://api.climatiq.io/data/v1/estimate', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${CLIMATIQ_API_KEY}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody)
            });

            if (!response.ok) {
                const errorData = await response.text();
                throw new Error(`API request failed with status ${response.status}: ${errorData}`);
            }

            return await response.json();
        } catch (error) {
            console.error(`Error calling ClimateIQ API for ${fuelKey}:`, error);
            throw error;
        }
    };

    const callSpendClimateIQAPI = async (fuelKey, spend, emissionFactor) => {
        try {
            const requestBody = {
                emission_factor: {
                    activity_id: emissionFactor.activity_id,
                    source: emissionFactor.source,
                    region: emissionFactor.region,
                    year: parseInt(emissionFactor.year),
                    data_version: "^3"
                },
                parameters: {
                    money: spend,
                    money_unit: 'usd'
                }
            };

            const response = await fetch('https://api.climatiq.io/data/v1/estimate', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${CLIMATIQ_API_KEY}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody)
            });

            if (!response.ok) {
                const errorData = await response.text();
                throw new Error(`API request failed with status ${response.status}: ${errorData}`);
            }

            return await response.json();
        } catch (error) {
            console.error(`Error calling ClimateIQ API for ${fuelKey}:`, error);
            throw error;
        }
    };

    const handleFuelToggle = (fuelKey) => {
        const fuels = { ...formData.fuels };
        fuels[fuelKey] = {
            ...fuels[fuelKey],
            selected: !fuels[fuelKey].selected,
            inputMethod: 'quantity', // Default to quantity
            spend: '',
            quantity: '',
            unit: fuelOptions.find(f => f.key === fuelKey)?.unit || 'm3'
        };
        updateState({ formData: { ...formData, fuels } });
    };

    const handleFuelDataChange = (fuelKey, field, value) => {
        const fuels = { ...formData.fuels };

        // If changing input method, clear the other field
        if (field === 'inputMethod') {
            fuels[fuelKey] = {
                ...fuels[fuelKey],
                inputMethod: value,
                spend: value === 'quantity' ? '' : fuels[fuelKey].spend,
                quantity: value === 'spend' ? '' : fuels[fuelKey].quantity
            };
        } else {
            fuels[fuelKey] = { ...fuels[fuelKey], [field]: value };
        }

        updateState({ formData: { ...formData, fuels } });
    };

    const handleCalculate = async () => {
        const selectedFuels = Object.entries(formData.fuels || {}).filter(([_, data]) => data.selected);
        if (selectedFuels.length === 0) {
            toastr.error('Please select at least one fuel type');
            return;
        }

        // Validate that each selected fuel has either quantity or spend based on input method
        for (const [fuelKey, data] of selectedFuels) {
            if (data.inputMethod === 'quantity' && (!data.quantity || isNaN(parseFloat(data.quantity)))) {
                toastr.error(`Please provide quantity for ${fuelOptions.find(f => f.key === fuelKey)?.label}`);
                return;
            }
            if (data.inputMethod === 'spend' && (!data.spend || isNaN(parseFloat(data.spend)))) {
                toastr.error(`Please provide annual spend for ${fuelOptions.find(f => f.key === fuelKey)?.label}`);
                return;
            }
        }

        if (Object.keys(state.availableFactors || {}).length === 0 && Object.keys(state.spendFactors || {}).length === 0) {
            toastr.error('Please search for emission factors first');
            return;
        }

        updateState({ isCalculating: true, calculationResults: {} });
        toastr.info('Calculating emissions via API...');

        try {
            const results = {};
            let hasSuccessfulCalculation = false;

            for (const [fuelKey, data] of selectedFuels) {
                try {
                    let apiResult;

                    if (data.inputMethod === 'quantity') {
                        const quantity = parseFloat(data.quantity);
                        const emissionFactor = state.availableFactors[fuelKey];

                        if (!emissionFactor) {
                            toastr.warning(`No quantity emission factor found for ${fuelKey}`);
                            continue;
                        }

                        apiResult = await callQuantityClimateIQAPI(fuelKey, quantity, emissionFactor);

                        results[fuelKey] = {
                            quantity,
                            unit: data.unit,
                            co2e: apiResult.co2e,
                            co2e_unit: apiResult.co2e_unit,
                            calculation_method: apiResult.co2e_calculation_method,
                            activity_data: apiResult.activity_data,
                            emission_factor: emissionFactor,
                            inputMethod: 'quantity'
                        };

                    } else { // spend-based
                        const spend = parseFloat(data.spend);
                        const emissionFactor = state.spendFactors[fuelKey];

                        if (!emissionFactor) {
                            toastr.warning(`No spend emission factor found for ${fuelKey}`);
                            continue;
                        }

                        apiResult = await callSpendClimateIQAPI(fuelKey, spend, emissionFactor);

                        results[fuelKey] = {
                            spend,
                            currency: 'USD',
                            co2e: apiResult.co2e,
                            co2e_unit: apiResult.co2e_unit,
                            calculation_method: apiResult.co2e_calculation_method,
                            activity_data: apiResult.activity_data,
                            emission_factor: emissionFactor,
                            inputMethod: 'spend'
                        };
                    }

                    const fuelLabel = fuelOptions.find(f => f.key === fuelKey)?.label;
                    toastr.success(`Calculated emissions for ${fuelLabel}: ${apiResult.co2e.toFixed(2)} ${apiResult.co2e_unit}`);
                    hasSuccessfulCalculation = true;

                } catch (error) {
                    console.error(`API error for ${fuelKey}:`, error);
                    const fuelLabel = fuelOptions.find(f => f.key === fuelKey)?.label;
                    toastr.error(`Failed to calculate emissions for ${fuelLabel}: ${error.message}`);
                }
            }

            updateState({ calculationResults: results });

            if (hasSuccessfulCalculation) {
                const totalEmissions = Object.values(results).reduce((sum, result) => sum + (result.co2e || 0), 0);
                toastr.success(`Calculation complete! Total emissions: ${totalEmissions.toFixed(2)} kg CO2e`);
            } else {
                toastr.warning('No emissions could be calculated. Please check your inputs and API key.');
            }
        } catch (error) {
            toastr.error('Calculation failed. Please check your inputs and try again.');
            console.error('Calculation error:', error);
        } finally {
            updateState({ isCalculating: false });
        }
    };

    const loadSampleData = () => {
        updateState({
            formData: {
                ...formData,
                year: '2025',
                fuels: {
                    naturalGas: { selected: true, inputMethod: 'quantity', spend: '', quantity: '1000', unit: 'm3' },
                    diesel: { selected: true, inputMethod: 'quantity', spend: '', quantity: '1000', unit: 'l' },
                    heavyFuelOil: { selected: true, inputMethod: 'spend', spend: '12000', quantity: '', unit: 'l' },
                    lpg: { selected: true, inputMethod: 'spend', spend: '6000', quantity: '', unit: 'kg' },
                    coal: { selected: true, inputMethod: 'quantity', spend: '', quantity: '30', unit: 't' }
                }
            }
        });
        toastr.info('Sample data loaded. Click "Search Factors" to find emission factors.');
    };

    return (
        <div className="max-w-4xl mx-auto px-4 py-8 pb-24">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
                <div className="p-8">
                    <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-sm text-yellow-800">
                            <strong>API Status:</strong> {CLIMATIQ_API_KEY && CLIMATIQ_API_KEY !== 'Y2ZBNWZKRzRGNzJYNjRQMzpa' ?
                                '✅ API Key Configured' : '❌ Please set REACT_APP_CLIMATIQ_API_KEY in .env file'}
                        </p>
                    </div>

                    <div className="flex justify-end mb-6">
                        <button
                            onClick={loadSampleData}
                            className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-all duration-200 text-sm"
                        >
                            Load Sample Data
                        </button>
                    </div>

                    <div className="mb-8 grid grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Year *</label>
                            <select
                                value={formData.year || new Date().getFullYear().toString()}
                                onChange={(e) => updateState({ formData: { ...formData, year: e.target.value } })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                                {Array.from({ length: new Date().getFullYear() - 2009 }, (_, i) =>
                                    (new Date().getFullYear() - i).toString()
                                ).map(year => (
                                    <option key={year} value={year}>{year}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Region</label>
                            <div className="flex items-center space-x-2">
                                <input
                                    type="text"
                                    value={formData.region || 'US'}
                                    disabled
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600"
                                />
                                <span className="text-sm text-gray-500">(United States)</span>
                            </div>
                        </div>
                    </div>

                    <div className="mb-6">
                        <button
                            onClick={handleSearchFactors}
                            disabled={state.isSearching}
                            className="px-6 py-3 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-all duration-200 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {state.isSearching ? (
                                <FaSpinner className="w-4 h-4 animate-spin" />
                            ) : (
                                <FaSearch className="w-4 h-4" />
                            )}
                            <span>{state.isSearching ? 'Searching...' : 'Search Emission Factors'}</span>
                        </button>
                    </div>

                    {(Object.keys(state.availableFactors || {}).length > 0 || Object.keys(state.spendFactors || {}).length > 0) && (
                        <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
                            <h3 className="font-semibold text-green-800 mb-2">Available Emission Factors</h3>
                            <div className="space-y-2">
                                {Object.entries(state.availableFactors || {}).map(([fuelKey, factor]) => (
                                    <div key={fuelKey} className="text-sm text-green-700">
                                        <strong>{fuelKey} (Quantity):</strong> {factor.name} ({factor.source}, {factor.region})
                                        <br />
                                        <span className="text-xs">Unit: {factor.unit} | Type: {factor.unit_type}</span>
                                    </div>
                                ))}
                                {Object.entries(state.spendFactors || {}).map(([fuelKey, factor]) => (
                                    <div key={fuelKey} className="text-sm text-green-700">
                                        <strong>{fuelKey} (Spend):</strong> {factor.name} ({factor.source}, {factor.region})
                                        <br />
                                        <span className="text-xs">Unit: {factor.unit} | Type: {factor.unit_type}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="mb-8">
                        <label className="block text-lg font-semibold text-gray-700 mb-4">
                            How much fuel does the company consume on site for its own consumption (i.e. not exported to the grid)?
                        </label>
                        <p className="text-sm text-gray-600 mb-4">
                            This information can often be found in fuel invoices, tank logs and/or maintenance logs.
                            Please provide your answer in volume (e.g. liters of diesel) and/or mass (kg) per site, per year.
                            Alternatively, EcoMina can calculate your onsite fuel generation based on how much you spend in US dollars, per year, and the unit price (i.e. per liter or per kg).
                        </p>

                        <div className="space-y-6">
                            {fuelOptions.map((fuel) => {
                                const fuelData = formData.fuels?.[fuel.key] || {
                                    selected: false,
                                    inputMethod: 'quantity',
                                    spend: '',
                                    quantity: '',
                                    unit: fuel.unit
                                };
                                const result = state.calculationResults?.[fuel.key];
                                const quantityFactor = state.availableFactors?.[fuel.key];
                                const spendFactor = state.spendFactors?.[fuel.key];

                                return (
                                    <div
                                        key={fuel.key}
                                        className={`border-2 rounded-lg p-6 transition-all ${fuelData.selected ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-white'
                                            }`}
                                    >
                                        <div className="flex items-start space-x-3 mb-4">
                                            <input
                                                type="checkbox"
                                                checked={fuelData.selected}
                                                onChange={() => handleFuelToggle(fuel.key)}
                                                className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500 mt-1"
                                            />
                                            <div className="flex-1">
                                                <label className="font-semibold text-gray-700 cursor-pointer">{fuel.label}</label>
                                                <p className="text-sm text-gray-600 mt-1">{fuel.description}</p>

                                                {quantityFactor && (
                                                    <div className="mt-2 p-2 bg-blue-100 rounded-lg">
                                                        <p className="text-xs text-blue-800">
                                                            <strong>Quantity Emission Factor:</strong> {quantityFactor.name} ({quantityFactor.source}, {quantityFactor.region})
                                                            <br />
                                                            <strong>Unit:</strong> {quantityFactor.unit} | <strong>Type:</strong> {quantityFactor.unit_type}
                                                        </p>
                                                    </div>
                                                )}

                                                {spendFactor && (
                                                    <div className="mt-2 p-2 bg-purple-100 rounded-lg">
                                                        <p className="text-xs text-purple-800">
                                                            <strong>Spend Emission Factor:</strong> {spendFactor.name} ({spendFactor.source}, {spendFactor.region})
                                                            <br />
                                                            <strong>Unit:</strong> {spendFactor.unit} | <strong>Type:</strong> {spendFactor.unit_type}
                                                        </p>
                                                    </div>
                                                )}

                                                {result && (
                                                    <div className="mt-2 p-2 bg-green-100 rounded-lg">
                                                        <p className="text-sm text-green-800 font-medium">
                                                            Emissions: {result.co2e.toFixed(2)} {result.co2e_unit}
                                                        </p>
                                                        <p className="text-xs text-green-600">
                                                            {result.inputMethod === 'quantity'
                                                                ? `Based on ${result.quantity} ${result.unit}`
                                                                : `Based on $${result.spend} USD`}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {fuelData.selected && (
                                            <div className="ml-8 space-y-4">
                                                <div className="flex space-x-4 mb-4">
                                                    <label className="flex items-center">
                                                        <input
                                                            type="radio"
                                                            name={`${fuel.key}-method`}
                                                            value="quantity"
                                                            checked={fuelData.inputMethod === 'quantity'}
                                                            onChange={(e) => handleFuelDataChange(fuel.key, 'inputMethod', e.target.value)}
                                                            className="w-4 h-4 text-green-600 border-gray-300 focus:ring-green-500"
                                                        />
                                                        <span className="ml-2 text-sm font-medium text-gray-700">Quantity Based</span>
                                                    </label>
                                                    <label className="flex items-center">
                                                        <input
                                                            type="radio"
                                                            name={`${fuel.key}-method`}
                                                            value="spend"
                                                            checked={fuelData.inputMethod === 'spend'}
                                                            onChange={(e) => handleFuelDataChange(fuel.key, 'inputMethod', e.target.value)}
                                                            className="w-4 h-4 text-green-600 border-gray-300 focus:ring-green-500"
                                                        />
                                                        <span className="ml-2 text-sm font-medium text-gray-700">Spend Based</span>
                                                    </label>
                                                </div>

                                                {fuelData.inputMethod === 'quantity' ? (
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                            Quantity ({fuel.unitLabel} per year) *
                                                        </label>
                                                        <div className="flex items-center space-x-2">
                                                            <input
                                                                type="number"
                                                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                                                                placeholder={`Enter ${fuel.unitLabel}`}
                                                                value={fuelData.quantity}
                                                                onChange={(e) => handleFuelDataChange(fuel.key, 'quantity', e.target.value)}
                                                            />
                                                            <span className="text-gray-600 text-sm">{fuel.unit}</span>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                            Annual Spend (US Dollars) *
                                                        </label>
                                                        <div className="flex items-center space-x-2">
                                                            <span className="text-gray-600">$</span>
                                                            <input
                                                                type="number"
                                                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                                                                placeholder="Enter amount"
                                                                value={fuelData.spend}
                                                                onChange={(e) => handleFuelDataChange(fuel.key, 'spend', e.target.value)}
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {Object.keys(state.calculationResults || {}).length > 0 && (
                        <div className="mt-8 p-6 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border border-green-200">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4">Calculation Results</h3>
                            <div className="space-y-2">
                                {Object.entries(state.calculationResults).map(([fuelKey, result]) => (
                                    <div key={fuelKey} className="flex justify-between items-center">
                                        <div>
                                            <span className="text-gray-700">
                                                {fuelOptions.find(f => f.key === fuelKey)?.label}
                                            </span>
                                            <span className="text-xs text-gray-500 ml-2">
                                                ({result.inputMethod === 'quantity'
                                                    ? `${result.quantity} ${result.unit}`
                                                    : `$${result.spend} USD`})
                                            </span>
                                        </div>
                                        <span className="font-semibold text-green-700">
                                            {result.co2e.toFixed(2)} {result.co2e_unit}
                                        </span>
                                    </div>
                                ))}
                                <div className="border-t border-gray-300 pt-2 mt-2">
                                    <div className="flex justify-between items-center font-bold text-lg">
                                        <span>Total Emissions:</span>
                                        <span className="text-blue-700">
                                            {Object.values(state.calculationResults).reduce((sum, result) => sum + (result.co2e || 0), 0).toFixed(2)} kg CO2e
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="mt-6 flex justify-end space-x-3">
                        <button
                            onClick={handleCalculate}
                            disabled={state.isCalculating || (Object.keys(state.availableFactors || {}).length === 0 && Object.keys(state.spendFactors || {}).length === 0)}
                            className="px-6 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-200 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {state.isCalculating ? (
                                <FaSpinner className="w-4 h-4 animate-spin" />
                            ) : (
                                <FaCalculator className="w-4 h-4" />
                            )}
                            <span>{state.isCalculating ? 'Calculating...' : 'Calculate'}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};


export default Api3Form;